import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

/**
 * How it Works — a public onboarding page for prospective and new players.
 *
 * Renders inside MainLayout (public route, no auth required) so the header,
 * league banner and sidebar stay consistent and the sidebar "How it Works"
 * link highlights. Content is three ordered steps (a genuine sequence, so the
 * numbered markers carry real meaning) followed by an FAQ accordion.
 */

interface Step {
  title: string;
  body: React.ReactNode;
}

const STEPS: Step[] = [
  {
    title: 'Create your team and join a division',
    body: (
      <>
        Signing up gives you a player account. You <em>join a league</em> by creating a team and
        enrolling it into a division — play <strong>singles</strong> on your own, or choose a{' '}
        <strong>doubles</strong> partner (they must already be registered and confirm your invite).
        The league admin then places your team at the appropriate tier in the division.
      </>
    ),
  },
  {
    title: 'Arrange and play your games',
    body: (
      <>
        In a single season you play one match against every other team in your tier. You can arrange
        a match by proposing a specific time and venue to your opponent through the app — or play it
        by your own arrangement.
      </>
    ),
  },
  {
    title: 'Record results and climb',
    body: (
      <>
        After a match, one player enters the score and the opponent approves it — then the standings
        update automatically. You earn ranking points with each game you play and record, and
        finishing top of your tier promotes you to a higher tier for the next season.
      </>
    ),
  },
];

interface Faq {
  q: string;
  a: React.ReactNode;
}

const FAQS: Faq[] = [
  {
    q: 'Do I need a team to play?',
    a: (
      <>
        Yes. Signing up creates your player account; to compete you create a team and enroll it into
        a division.
      </>
    ),
  },
  {
    q: 'Singles or doubles — how do I add a partner?',
    a: (
      <>
        For doubles or mixed you pick a partner who&apos;s already registered; they accept the
        invite to activate the team. Singles skips this step — you&apos;re the whole team.
      </>
    ),
  },
  {
    q: 'What are categories, divisions and tiers?',
    a: (
      <>
        A <strong>category</strong> is the event (for example, Men&apos;s Singles). A{' '}
        <strong>division</strong> is a named competition inside it. A <strong>tier</strong> is one
        level of that division (Tier I is the top), and you play against the other teams in your
        tier. Promotion and relegation move teams between tiers.
      </>
    ),
  },
  {
    q: 'How are games scheduled?',
    a: (
      <>
        You claim one of your fixtures, propose time slots and a venue, and your opponent accepts or
        counters. There&apos;s no home or away — it&apos;s just a match between two teams. You can
        also agree a match privately and simply record the result.
      </>
    ),
  },
  {
    q: 'Does the league admin book or pay for the court?',
    a: (
      <>
        No. You or your opponent book the court, and you split the court charge between the two
        teams.
      </>
    ),
  },
  {
    q: 'Does the league admin provide shuttles?',
    a: (
      <>No. You and your opponent each contribute an equal number of shuttles to play the match.</>
    ),
  },
  {
    q: 'Can we play with plastic shuttles?',
    a: (
      <>
        Normally every division is played with feather shuttles. Contact your league admin about any
        exceptions.
      </>
    ),
  },
  {
    q: 'What are the scoring rules?',
    a: (
      <>
        Matches are best of three games. The league admin sets the scoring rules for each division —
        either 21-point (win by 2, cap 30) or 15-point (win by 2, cap 21).
      </>
    ),
  },
  {
    q: 'How are results confirmed, and what if a score is wrong?',
    a: (
      <>
        Your opponent approves the submitted score before it counts. If it&apos;s wrong you can
        dispute it within the dispute window, and a league admin resolves it.
      </>
    ),
  },
  {
    q: 'How do ranking points, standings and seasons work?',
    a: (
      <>
        You earn ranking points for each result. Teams level on points are ranked by game
        difference, then point difference, then head-to-head. Each season ends with promotion and
        relegation between tiers, and points reset for the new season.
      </>
    ),
  },
  {
    q: 'Do I get email notifications?',
    a: (
      <>
        Once your email address is verified, you can receive optional &ldquo;you have something to
        do&rdquo; emails — a proposal to answer, or a result to approve. You control these any time
        under <strong>Profile → My Preferences</strong>.
      </>
    ),
  },
  {
    q: 'Can I play in more than one category or division?',
    a: (
      <>
        Yes. You can enter teams in multiple categories, and a team can be enrolled in more than one
        division — each carries its own standings.
      </>
    ),
  },
  {
    q: "What if a match can't be finished?",
    a: (
      <>
        You can report it as a retirement (injury or withdrawal) or a walkover — the non-retiring
        team is recorded as the winner. A league admin can also mark a match as abandoned when it
        can&apos;t be completed or resolved normally.
      </>
    ),
  },
  {
    q: 'Do I need an account to browse?',
    a: (
      <>
        No. You can explore leagues, divisions, teams, games and standings without logging in — you
        only need an account to create a team and play.
      </>
    ),
  },
];

const HowItWorksPage: React.FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [openFaqs, setOpenFaqs] = useState<Set<number>>(new Set());

  const toggleFaq = (index: number) => {
    setOpenFaqs(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="bg-white rounded-lg shadow-sm p-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">
          For new players
        </p>
        <h1 className="mt-2 text-3xl font-bold text-gray-900 sm:text-4xl">How Sulis-Liiga works</h1>
        <p className="mt-3 text-gray-600 max-w-2xl">
          From sign-up to the top of your tier — the whole thing in three steps.
        </p>
      </section>

      {/* ── Three steps ──────────────────────────────────────────────────── */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900">Three steps to get playing</h2>
        <ol className="mt-4 space-y-4">
          {STEPS.map((step, index) => (
            <li key={step.title} className="bg-white rounded-lg shadow-sm p-6 flex gap-4 sm:gap-5">
              <span
                className="shrink-0 h-10 w-10 rounded-full bg-primary-600 text-white flex items-center justify-center text-lg font-bold select-none"
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  <span className="sr-only">{`Step ${index + 1}: `}</span>
                  {step.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900">Frequently asked questions</h2>
        <div className="mt-4 bg-white rounded-lg shadow-sm divide-y divide-gray-200">
          {FAQS.map((faq, index) => {
            const isOpen = openFaqs.has(index);
            const panelId = `faq-panel-${index}`;
            const buttonId = `faq-button-${index}`;
            return (
              <div key={faq.q}>
                <h3>
                  <button
                    type="button"
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                  >
                    <span className="text-sm font-medium text-gray-900">{faq.q}</span>
                    <svg
                      className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </h3>
                {isOpen && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className="px-5 pb-4 -mt-1 text-sm leading-relaxed text-gray-600"
                  >
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Closing call-to-action ───────────────────────────────────────── */}
      <section className="mt-8 mb-2">
        <div className="bg-primary-50 border border-primary-100 rounded-lg p-6 sm:flex sm:items-center sm:justify-between gap-4">
          <p className="text-base font-medium text-gray-900">
            {isAuthenticated ? 'Ready to play? Create your team.' : 'Ready to play?'}
          </p>
          <div className="mt-4 sm:mt-0 flex flex-wrap gap-3">
            {isAuthenticated ? (
              <Link
                to="/teams/new"
                className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                Create Team
              </Link>
            ) : (
              <Link
                to="/register"
                className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
              >
                Sign up
              </Link>
            )}
            <Link
              to="/divisions"
              className="px-5 py-2.5 rounded-md text-sm font-medium text-primary-600 border border-primary-600 hover:bg-primary-100 transition-colors"
            >
              Browse divisions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
